import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Clock, Download, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { k: "customers", label: "Customers" },
  { k: "pets", label: "Pets" },
  { k: "customers_pets", label: "Customers + Pets" },
];
const hasPerm = (u) => u?.role === "owner" || u?.role === "admin" || (u?.effective_permissions || u?.permissions || []).includes("data_import.manage") || (u?.permissions || []).includes("*");

export default function DataImport() {
  const { user } = useAuth();
  if (!hasPerm(user)) {
    return <div className="mx-auto max-w-2xl mt-10 rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="import-no-access">
      <AlertTriangle className="mx-auto h-6 w-6 text-slate-300" /><div className="mt-2 font-medium text-slate-700">You don’t have access to Data Import</div>
      <div className="mt-1 text-sm text-slate-500">Ask an owner or admin for the Data Import permission.</div></div>;
  }
  return <Wizard />;
}

function Wizard() {
  const [step, setStep] = useState(1);
  const [type, setType] = useState("customers_pets");
  const [job, setJob] = useState(null);
  const [mapping, setMapping] = useState({});
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [review, setReview] = useState(null);
  const [tab, setTab] = useState("valid");
  const [mode, setMode] = useState("skip_duplicates");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data.items || r.data || [])).catch(() => {}); }, []);

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("import_type", type);
    try {
      const r = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(r.data); setMapping(r.data.suggested_mapping || {});
      setStep(r.data.sheets?.length > 1 ? 3 : 4);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/import/${job.id}/validate`, { mapping, branch_id: branchId, sheet: job.sheet });
      setReview(r.data); setStep(5);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      await api.post(`/import/${job.id}/commit`, { mode });
      setStep(6); pollStatus();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const [status, setStatus] = useState(null);
  const pollStatus = () => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/import/${job.id}`);
        setStatus(r.data);
        if (["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"].includes(r.data.status)) clearInterval(t);
      } catch { clearInterval(t); }
    }, 1200);
  };

  const STEPS = ["Type", "Upload", "Sheet", "Map", "Review", "Done"];
  return (
    <div data-testid="data-import-page">
      <PageHeader title="Data Import" subtitle="Bring your existing customers and pets into Pawkt">
        <ImportHistoryButton />
      </PageHeader>
      <div className="mb-5 flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <div key={s} className={`rounded-full px-3 py-1 ${step >= i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{i + 1}. {s}</div>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="import-step-type" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="font-medium text-slate-800">What would you like to import?</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {TYPES.map((t) => (
              <button key={t.k} data-testid={`import-type-${t.k}`} onClick={() => setType(t.k)}
                className={`rounded-xl border p-4 text-left ${type === t.k ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="font-semibold text-slate-800">{t.label}</div>
              </button>
            ))}
          </div>
          <Button data-testid="import-next-type" onClick={() => setStep(2)} className="mt-5 bg-slate-900 text-white gap-1.5">Continue <ArrowRight className="h-4 w-4" /></Button>
        </div>
      )}

      {step === 2 && (
        <div data-testid="import-step-upload" className="rounded-xl border border-slate-200 bg-white p-6">
          <TemplatePanel />
          <div className="rounded-xl border-2 border-dashed border-slate-200 p-10 text-center">
            <Upload className="mx-auto h-8 w-8 text-slate-300" />
            <div className="mt-2 text-sm text-slate-600">Upload a CSV, XLSX, XLS, TSV or JSON file</div>
            <input ref={fileRef} type="file" data-testid="import-file-input" className="hidden"
              accept=".csv,.tsv,.json,.xlsx,.xls" onChange={(e) => upload(e.target.files[0])} />
            <Button data-testid="import-choose-file" onClick={() => fileRef.current?.click()} disabled={busy} className="mt-4 bg-slate-900 text-white">{busy ? "Reading…" : "Choose file"}</Button>
            <div className="mt-2 text-xs text-slate-400">Supported formats: CSV, XLSX, XLS, TSV, JSON. Max 25 MB / 100,000 rows.</div>
          </div>
          <Button variant="outline" onClick={() => setStep(1)} className="mt-4 gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        </div>
      )}

      {step === 3 && job && (
        <div data-testid="import-step-sheet" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="font-medium text-slate-800">Select worksheet</div>
          <select data-testid="import-sheet-select" value={job.sheet || job.sheets[0]}
            onChange={(e) => setJob({ ...job, sheet: e.target.value })}
            className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            {job.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="mt-4"><Button data-testid="import-next-sheet" onClick={() => setStep(4)} className="bg-slate-900 text-white">Continue</Button></div>
        </div>
      )}

      {step === 4 && job && (
        <div data-testid="import-step-map" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="text-sm text-slate-600"><FileSpreadsheet className="inline h-4 w-4" /> {job.filename} · {job.file_type?.toUpperCase()} · {job.row_count} rows</div>
          <div className="mt-3 font-medium text-slate-800">Map your columns</div>
          <div className="mt-3 space-y-2">
            {job.columns.map((col) => (
              <div key={col} className="flex items-center gap-3">
                <div className="w-1/2 truncate text-sm text-slate-700">{col}</div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
                <select data-testid={`import-map-${col}`} value={mapping[col] || ""} onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })}
                  className={`w-1/2 rounded-md border px-2 py-1.5 text-sm ${mapping[col] ? "border-slate-200" : "border-amber-300 bg-amber-50"}`}>
                  <option value="">Needs your selection…</option>
                  {job.target_fields.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                </select>
              </div>
            ))}
          </div>
          {branches.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-slate-500 mb-1">Assign to branch</div>
              <select data-testid="import-branch-select" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Default / from file</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="mt-5 flex gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button data-testid="import-validate-btn" onClick={validate} disabled={busy} className="bg-slate-900 text-white">{busy ? "Checking…" : "Review records"}</Button>
          </div>
        </div>
      )}

      {step === 5 && review && (
        <div data-testid="import-step-review" className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="grid gap-3 sm:grid-cols-5">
            {[["total", "Total", "text-slate-800"], ["valid", "Ready", "text-emerald-600"], ["warning", "Warnings", "text-amber-600"], ["duplicate", "Duplicates", "text-sky-600"], ["error", "Errors", "text-rose-600"]].map(([k, l, c]) => (
              <div key={k} data-testid={`import-count-${k}`} className="rounded-lg border border-slate-200 p-3 text-center">
                <div className={`font-display text-2xl font-bold ${c}`}>{review.counts[k]}</div><div className="text-xs text-slate-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-500">Nothing has been imported yet.</div>
          <div className="mt-4 flex gap-1 border-b border-slate-200 text-sm">
            {["valid", "warning", "duplicate", "error"].map((t) => (
              <button key={t} data-testid={`import-tab-${t}`} onClick={() => setTab(t)} className={`px-3 py-2 ${tab === t ? "border-b-2 border-emerald-500 text-slate-800" : "text-slate-400"}`}>{t} ({review.counts[t]})</button>
            ))}
          </div>
          <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs"><thead className="bg-slate-50 text-left text-slate-500"><tr><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Pet</th><th className="px-2 py-1.5">Issues</th></tr></thead>
              <tbody>{(review.samples[tab] || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1.5">{r.row}</td>
                  <td className="px-2 py-1.5">{r.customer?.name || "—"}</td>
                  <td className="px-2 py-1.5">{r.pet?.name ? `${r.pet.name} (${r.pet.breed_canonical || r.pet.breed || "—"})` : "—"}</td>
                  <td className="px-2 py-1.5 text-slate-500">{[...(r.errors || []), ...(r.warnings || [])].map((e) => e[1]).join("; ") || (r.duplicate ? "Possible duplicate — review" : "")}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <select data-testid="import-mode-select" value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              <option value="skip_duplicates">Skip duplicates</option>
              <option value="import_new">Import new records</option>
            </select>
            <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/import/${job.id}/errors`, "_blank")} className="gap-1.5" data-testid="import-download-errors"><Download className="h-4 w-4" /> Error report</Button>
            <Button data-testid="import-commit-btn" onClick={commit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">Import valid records</Button>
          </div>
        </div>
      )}

      {step === 6 && (
        <div data-testid="import-step-done" className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          {status && ["COMPLETED", "COMPLETED_WITH_ERRORS"].includes(status.status) ? (<>
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <div className="mt-2 font-display text-xl font-bold text-slate-800">Import {status.status === "COMPLETED_WITH_ERRORS" ? "completed with some errors" : "complete"}</div>
            <div className="mt-2 text-sm text-slate-600" data-testid="import-result">
              {status.result?.customers_created || 0} customers · {status.result?.pets_created || 0} pets created · {status.result?.skipped || 0} skipped · {status.result?.failed || 0} failed
            </div>
          </>) : (<>
            <Clock className="mx-auto h-10 w-10 animate-pulse text-slate-400" />
            <div className="mt-2 font-medium text-slate-700">Importing…</div>
            <div className="mt-1 text-sm text-slate-500" data-testid="import-progress">{status?.progress || 0} / {job?.row_count || 0}</div>
          </>)}
        </div>
      )}
    </div>
  );
}

const TEMPLATE_TYPES = [
  { k: "customers", label: "Customers" },
  { k: "pets", label: "Pets" },
  { k: "customers_pets", label: "Customers + Pets" },
];

function TemplatePanel() {
  const dl = (t, fmt) => window.open(`${api.defaults.baseURL}/import/template?import_type=${t}&fmt=${fmt}`, "_blank");
  return (
    <div data-testid="import-template-panel" className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <Download className="h-4 w-4 text-emerald-600" /> Download sample templates
      </div>
      <div className="mt-1 text-xs text-slate-500">Each template shows the exact columns with example rows — remove the example rows before uploading.</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {TEMPLATE_TYPES.map((t) => (
          <div key={t.k} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-sm font-semibold text-slate-800">{t.label}</div>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid={`import-template-${t.k}-csv`} onClick={() => dl(t.k, "csv")}><Download className="h-3 w-3" /> CSV</Button>
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid={`import-template-${t.k}-xlsx`} onClick={() => dl(t.k, "xlsx")}><Download className="h-3 w-3" /> XLSX</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportHistoryButton() {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  useEffect(() => { if (open) api.get("/import/jobs").then((r) => setJobs(r.data.items || [])); }, [open]);
  return (<>
    <Button variant="outline" data-testid="import-history-btn" onClick={() => setOpen(!open)} className="gap-1.5"><Clock className="h-4 w-4" /> Import history</Button>
    {open && (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setOpen(false)}>
        <div className="h-full w-full max-w-md overflow-y-auto bg-white p-5" onClick={(e) => e.stopPropagation()} data-testid="import-history-panel">
          <div className="font-display text-lg font-bold">Import history</div>
          {jobs.length === 0 ? <div className="mt-4 text-sm text-slate-500">No imports yet.</div> : (
            <div className="mt-3 space-y-2">{jobs.map((j) => (
              <div key={j.id} data-testid={`import-hist-${j.id}`} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex justify-between"><span className="font-medium text-slate-800">{j.filename}</span><span className="text-xs text-slate-500">{j.status}</span></div>
                <div className="text-xs text-slate-500">{j.import_type} · {j.row_count} rows · {j.result ? `${j.result.customers_created || 0} cust / ${j.result.pets_created || 0} pets` : "—"}</div>
              </div>))}</div>
          )}
        </div>
      </div>
    )}
  </>);
}
